import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Modal from '@material-ui/core/Modal';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import RequiredButton from '../../components/atoms/RequiredButton';
import AnyButton from '../../components/atoms/AnyButton';
import CancelButton from '../../components/atoms/CancelButton';
import ConfirmButton from '../../components/atoms/ConfirmButton';
import FileUpload from '../../components/molecules/FileUpload';
import GroupGochiMenuAddConfirm from './GroupGochiMenuAddConfirm';
import { resizeImage, uploadFile, sendLogin } from '../../auth/Auth';
import {
  getGroupGochiMenus,
  addGroupGochiMenu,
  addGroupGochiMenuImage
} from '../../actions/GroupGochiMenus';
import { getGroups } from '../../actions/Groups';
import ValidationText from '../../components/atoms/ValidationText';

const styles = {
  root: {
    position: 'absolute',
    width: '85%',
    height: 'auto',
    maxHeight: '80%',
    top: '5%',
    left: '2%',
    backgroundColor: '#fff',
    padding: 28,
    overflow: 'scroll',
    outline: 'none'
  },
  pageTitle: {
    margin: '20px 0px 0px',
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  mainPaper: {
    position: 'relative',
    margin: '1px 0px 10px',
    padding: '10px 26px 10px 26px'
  },
  editTable: {
    width: 'auto'
  },
  tableCellBorderNone: {
    borderBottom: 'none'
  },
  textField100: {
    padding: '0px 5px 0px 5px',
    width: '100px'
  },
  textField: {
    padding: '0px 5px 0px 5px',
    width: '300px'
  },
  textFieldLong: {
    padding: '0px 5px 0px 5px',
    width: '500px'
  },
  button: {
    margin: '5px auto'
  },
  priceCalc: {
    margin: '0px 5px',
    color: '#aaa'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      name: state.loginInfo.name,
      detail: state.groupGochiMenus.detail,
      groups: state.groups.groups,
      taxTypes: state.gochiMenus.taxTypes,
      reducedTaxMenuPrefix: state.commons.reducedTaxMenuPrefix
    }),
    dispatch => ({
      actions: bindActionCreators(
        {
          getGroups,
          getGroupGochiMenus,
          addGroupGochiMenu,
          addGroupGochiMenuImage
        },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openAddProgress', 'updateOpenAddProgress', false),
  withState('loading', 'updateLoading', false),
  withState('isAddSuccess', 'updateIsAddSuccess', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('groupId', 'setGroupId', 0),
  withState('groupName', 'setGroupName', ''),
  withState('menuName', 'setMenuName', ''),
  withState('menuNameError', 'setMenuNameError', ''),
  withState('price', 'setPrice', ''),
  withState('priceError', 'setPriceError', ''),
  withState('priceCalc', 'setPriceCalc', '0 (税抜 0)'),
  withState('description', 'setDescription', ''),
  withState('taxType', 'setTaxType', 'EXTERNAL'),
  withState('reducedTaxCheck', 'setReducedTaxCheck', false),
  withState('reducedTaxType', 'setReducedTaxType', 'UNREDUCED'),
  withState('tagStr', 'setTagStr', ''),
  withState('tags', 'setTags', null),
  withState('image', 'setImage', null),
  withState('previewImage', 'setPreviewImage', null),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    }
  }),
  lifecycle({
    async componentDidMount() {
      const groupData = await this.props.actions
        .getGroups()
        .then(data => this.props.checkSession(data));
      if (groupData.type === 'GET_GROUPS_SUCCESS') {
        this.props.setGroupId(groupData.payload.groups[0].groupId);
        this.props.setGroupName(groupData.payload.groups[0].groupName);
      }
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    searchKey,
    openAddConfirm,
    updateOpenAdd,
    updateOpenAddConfirm,
    openAddProgress,
    updateOpenAddProgress,
    loading,
    updateLoading,
    isAddSuccess,
    updateIsAddSuccess,
    resultMessage,
    setResultMessage,
    groupId,
    setGroupId,
    groupName,
    setGroupName,
    groups,
    menuName,
    setMenuName,
    price,
    setPrice,
    priceCalc,
    setPriceCalc,
    description,
    setDescription,
    taxType,
    setTaxType,
    taxTypes,
    reducedTaxCheck,
    setReducedTaxCheck,
    reducedTaxType,
    setReducedTaxType,
    tagStr,
    setTagStr,
    tags,
    setTags,
    image,
    setImage,
    previewImage,
    setPreviewImage,
    reducedTaxMenuPrefix,
    menuNameError,
    setMenuNameError,
    priceError,
    setPriceError
  }) => {
    const addConfirm = {
      groupName,
      menuName,
      price,
      description,
      taxType,
      reducedTaxCheck,
      tagStr,
      previewImage
    };
    const addParam = {
      groupId,
      menuName,
      price,
      description,
      taxType,
      reducedTaxType,
      tags
    };

    const errors = {
      menuNameError,
      priceError
    };
    const validMenuName = () => {
      const value = menuName;
      if (value === '') {
        setMenuNameError('必須項目です。');
        return 1;
      }
      setMenuNameError('');
      setMenuName(value);
      return 0;
    };
    const validPrice = () => {
      const value = price;
      if (value === '') {
        setPriceError('必須項目です。');
        return 1;
      }
      if (!/^\d+$/.test(value)) {
        setPriceError('半角数字のみ入力してください。');
        return 1;
      }
      setPriceError('');
      setPrice(value);
      return 0;
    };
    const valid = () => {
      const errorCount = validMenuName() + validPrice();
      if (errorCount === 0) {
        return true;
      }
      return false;
    };

    const cancelAction = () => updateOpenAdd(false);
    const confirmAction = () => {
      if (!valid()) {
        alert('登録情報を正しく入力してください。');
      } else {
        updateOpenAddConfirm(true);
      }
    };
    const backAction = () => updateOpenAddConfirm(false);
    const registerAction = async () => {
      updateLoading(true);
      updateOpenAddProgress(true);
      const addedGroupMenu = await actions
        .addGroupGochiMenu(addParam)
        .then(data => checkSession(data));
      if (image !== null) {
        let imageData = image;
        if (imageData.size > Number(process.env.REACT_APP_RESIZE_BORDER)) {
          const { resizeImageFile } = await resizeImage(imageData);
          imageData = resizeImageFile;
        }
        const imagePath = await uploadFile(
          'menu',
          addedGroupMenu.payload.menuId,
          imageData,
          'public'
        );
        actions
          .addGroupGochiMenuImage(addedGroupMenu.payload.menuId, {
            image: imagePath
          })
          .then(data => checkSession(data));
      }
      if (addedGroupMenu.type === 'ADD_GROUP_GOCHI_MENU_SUCCESS') {
        updateIsAddSuccess(true);
        actions.getGroupGochiMenus(searchKey).then(data => checkSession(data));
        setResultMessage('登録が完了しました。');
        updateLoading(false);
      } else {
        updateIsAddSuccess(false);
        setResultMessage('登録に失敗しました。');
        updateLoading(false);
      }
    };

    const progressOkAction = () => {
      if (isAddSuccess) {
        updateOpenAdd(false);
        updateOpenAddConfirm(false);
        updateOpenAddProgress(false);
      } else {
        updateOpenAddProgress(false);
      }
    };

    const calcPrice = e => {
      let includedTaxPrice = 0;
      let excludedTaxPrice = 0;

      let latestPrice = price;
      if (e.target.id === 'price') {
        latestPrice = e.target.value;
      }

      let latestTaxType = taxType;
      if (e.target.name === 'taxType') {
        latestTaxType = e.target.value;
      }

      // TODO DBから取得
      let tax = 0.1;
      if (
        (e.target.id !== 'reducedTax' && reducedTaxCheck) ||
        (e.target.id === 'reducedTax' && e.target.checked)
      ) {
        // TODO DBから取得
        tax = 0.08;
      }

      if (latestPrice !== '') {
        if (latestTaxType === 'INTERNAL') {
          includedTaxPrice = latestPrice;
          excludedTaxPrice =
            latestPrice - Math.floor((latestPrice * tax) / (1 + tax));
        } else {
          includedTaxPrice = Math.floor(latestPrice * (1 + tax));
          excludedTaxPrice = latestPrice;
        }
      }

      setPriceCalc(`${includedTaxPrice} (税抜 ${excludedTaxPrice})`);
    };
    const splitTagStr = str => {
      if (str === '') {
        setTags(null);
      } else {
        setTags(
          str.split(/\s+/).map(t => {
            return { tagName: t };
          })
        );
      }
    };
    const convertReducedTax = checked => {
      if (checked) {
        setReducedTaxType('REDUCED');
      } else {
        setReducedTaxType('UNREDUCED');
      }
    };
    const setGroupNameData = id => {
      groups.forEach(item => {
        if (id === item.groupId) {
          setGroupName(item.groupName);
        }
      });
    };

    return (
      <div className={classes.root}>
        <div className={classes.pageTitle}>
          <Typography variant="h6">グループメニュー登録</Typography>
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          <Table className={classes.editTable}>
            <TableRow>
              <TableCell>
                <RequiredButton />
              </TableCell>
              <TableCell>
                <TextField
                  required
                  select
                  label="グループ名"
                  className={classes.textField}
                  margin="normal"
                  value={groupId}
                  onChange={e => {
                    setGroupId(e.target.value);
                    setGroupNameData(e.target.value);
                  }}
                  InputLabelProps={{
                    shrink: true
                  }}
                >
                  {groups.map(item => {
                    return (
                      <MenuItem key={item.groupId} value={item.groupId}>
                        {item.groupName}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell>
                <RequiredButton />
              </TableCell>
              <TableCell>
                <TextField
                  required
                  label="メニュー名"
                  className={classes.textField}
                  margin="normal"
                  onChange={e => {
                    setMenuName(e.target.value);
                  }}
                  onBlur={validMenuName}
                />
                <ValidationText message={errors.menuNameError} />
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell>
                <RequiredButton />
              </TableCell>
              <TableCell>
                <TextField
                  id="price"
                  required
                  label="価格"
                  className={classes.textField}
                  margin="normal"
                  onChange={e => {
                    setPrice(e.target.value);
                    calcPrice(e);
                  }}
                  onBlur={validPrice}
                />
                <ValidationText message={errors.priceError} />
                <div className={classes.priceCalc}>{priceCalc}</div>
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell>
                <RequiredButton />
              </TableCell>
              <TableCell>
                <div>
                  <TextField
                    id="taxType"
                    name="taxType"
                    select
                    label="税区分・寄付"
                    className={classes.textField}
                    margin="normal"
                    value={taxType}
                    onChange={e => {
                      setTaxType(e.target.value);
                      calcPrice(e);
                    }}
                    InputLabelProps={{
                      shrink: true
                    }}
                  >
                    {taxTypes.map(group => {
                      return (
                        <MenuItem key={group.id} value={group.id}>
                          {group.name}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </div>
                <FormControlLabel
                  control={<Checkbox id="reducedTax" color="primary" />}
                  label="軽減税率対象"
                  value={reducedTaxCheck}
                  onChange={e => {
                    setReducedTaxCheck(e.target.checked);
                    convertReducedTax(e.target.checked);
                    calcPrice(e);
                  }}
                />
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell>
                <AnyButton />
              </TableCell>
              <TableCell>
                <TextField
                  label="説明"
                  multiline
                  rows="4"
                  className={classes.textFieldLong}
                  margin="normal"
                  onChange={e => {
                    setDescription(e.target.value);
                  }}
                />
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell>
                <AnyButton />
              </TableCell>
              <TableCell>
                <TextField
                  label="タグ"
                  className={classes.textFieldLong}
                  margin="normal"
                  onChange={e => {
                    setTagStr(e.target.value);
                    splitTagStr(e.target.value);
                  }}
                />
              </TableCell>
              <TableCell>
                #おすすめ #おふくろの味 #学生に ＃ガッツリ
                などメニューの特徴を表すタグを記載できます。
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellBorderNone}>
                <AnyButton />
              </TableCell>
              <TableCell className={classes.tableCellBorderNone}>
                <FileUpload
                  label="image"
                  setFile={setImage}
                  previewImage={previewImage}
                  setPreviewImage={setPreviewImage}
                />
              </TableCell>
              <TableCell className={classes.tableCellBorderNone} />
            </TableRow>
          </Table>
        </Paper>
        <div>
          <CancelButton onClickAction={cancelAction} color="primary" />
          <ConfirmButton onClickAction={confirmAction} />
        </div>
        <Modal open={openAddConfirm}>
          <GroupGochiMenuAddConfirm
            backAction={backAction}
            registerAction={registerAction}
            addConfirm={addConfirm}
            reducedTaxMenuPrefix={reducedTaxMenuPrefix}
            openAddProgress={openAddProgress}
            progressOkAction={progressOkAction}
            loading={loading}
            resultMessage={resultMessage}
          />
        </Modal>
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
